import { useState, useEffect, memo } from "react";
import styled from "styled-components";
import { OrderProps } from "styled-system";
import { useLocation } from "react-router";

import Tab from "../Tab";
import Box from "../Box";

import useBreakPoint from "../../utils/useBreakPoint";
import { isEmailInWhitelist } from "../../utils/whiteListUtils";

import {
  useOptionNpsBarometer,
  useOptionNpsDrive,
  useOptionNpsServices,
  useOptionNpsStore,
  useOptionRegion,
} from "../../utils/options";

import { useTranslation } from "react-i18next";
import { useKpiVisibility } from "../../kpis/useKpiVisibility";

import {
  getModeFromPath,
  isRegionMode,
  isStoreMode,
} from "../../utils/modeUtils";

import { useAuth, User } from "../../Auth/AuthProvider";
import { useRange } from "../../context/rangeContext";

type TabsBarProps = OrderProps;

/* -------------------- STYLES -------------------- */

const DesktopContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const DesktopScroll = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 16px;

  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const MobileScroll = styled(Box)`
  position: absolute;
  bottom: 0;

  gap: 16px;

  white-space: nowrap;
  overflow-x: scroll;
  overflow-y: hidden;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/* -------------------- SHARED TABS -------------------- */

const TabsContent = memo(
({
  activeTab,
}: {
  activeTab: string;
}) => {
  const { t } = useTranslation();

  const optionsNpsStore = useOptionNpsStore();
  const optionNpsDrive = useOptionNpsDrive();
  const optionNpsBarometer = useOptionNpsBarometer();
  const optionNpsServices = useOptionNpsServices();

  const regionOptions = useOptionRegion();

  const { getKpiRange } = useRange();
  const { isModuleExists } = useKpiVisibility();

  const { user } = useAuth();

  const mode = getModeFromPath(activeTab);
  const regionRange = getKpiRange("kpi_region");

  return (
    <>
      {isStoreMode(mode) && (
        <>
          {isModuleExists("Commercial") && (
            <Tab
              tabName={t("commercial.commercial")}
              isActive={activeTab.includes("/commercial")}
              page="/commercial"
              search="?byHour=SDV&sector=0"
            />
          )}

          {isModuleExists("Client") && (
            <Tab
              tabName={t("client.client")}
              isActive={activeTab === "/client"}
              page="/client"
              search={`?npsStore=${
                getKpiRange("npsStore")
                || optionsNpsStore?.[1]?.value
              }&npsDrive=${
                getKpiRange("npsDrive")
                || optionNpsDrive?.[0]?.value
              }&npsService=${
                getKpiRange("npsService")
                || optionNpsServices?.[0]?.value
              }&npsBaro=${
                getKpiRange("npsBaro")
                || optionNpsBarometer?.[0]?.value
              }`}
            />
          )}

          {isModuleExists("Operationnel") && (
            <Tab
              tabName={t("operationnel.operationnel")}
              isActive={activeTab.includes("/operational")}
              page="/operational"
            />
          )}

          {isModuleExists("Ecommerce") && (
            <Tab
              tabName={t("ecommerce.ecommerce")}
              isActive={activeTab.includes("/ecom")}
              page="/ecom"
            />
          )}
        </>
      )}

      {isRegionMode(mode) &&
        (
          isEmailInWhitelist(
            "ALERT_WHITE_LIST",
            user
          )
          || !window?.config?.CREDENTIALS?.ALERT_WHITE_LIST
          || window?.config?.CREDENTIALS?.ALERT_WHITE_LIST
            .length === 0
        )
        && !isUserFranchise(user) && (
          <>
            <Tab
              tabName={t("territoire.synthese_magasins")}
              isActive={[
                "/region/direct",
                "/region/week",
                "/region/j-1",
                "/region/month",
              ].includes(activeTab)}
              page={
                regionRange
                  ? (regionOptions as any).find(
                      (opt: any) =>
                        opt.range === regionRange
                    )?.value || "/region/direct"
                  : "/region/direct"
              }
            />

            <Tab
              tabName={t("territoire.synthese_alertes")}
              isActive={
                activeTab === "/region/alertes"
              }
              page="/region/alertes"
            />
          </>
      )}
    </>
  );
});

TabsContent.displayName = "TabsContent";

/* -------------------- MOBILE VIEW -------------------- */

const MobileTabs = memo(
({
 activeTab,
 ...rest
}: TabsBarProps & { activeTab: string }) => (
  <MobileScroll
    display="flex"
    flexDirection="row"
    justifyContent={["normal", "space-around"]}
    alignItems="center"
    width="100%"
    pl={4}
    data-testid="tabs"
    className="tabs-bar"
    {...rest}
  >
    <TabsContent activeTab={activeTab} />
  </MobileScroll>
));

MobileTabs.displayName = "MobileTabs";

/* -------------------- DESKTOP VIEW -------------------- */

const DesktopTabs = memo(
({
 activeTab,
 ...rest
}: TabsBarProps & { activeTab: string }) => (
  <DesktopContainer>
    <DesktopScroll
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="flex-start"
      width="100%"
      className="tabs-bar"
      data-testid="tabs"
      {...rest}
    >
      <TabsContent activeTab={activeTab} />
    </DesktopScroll>
  </DesktopContainer>
));

DesktopTabs.displayName = "DesktopTabs";

/* -------------------- ROOT -------------------- */

const TabsBar = (props: TabsBarProps) => {
  const currentView = useBreakPoint();
  const isDesktop = currentView === "desktop";
  const { user } = useAuth();

  const router = useLocation();

  const [activeTab, setActiveTab] = useState(
    router.pathname
  );

  useEffect(() => {
    setActiveTab(router.pathname);
  }, [router.pathname]);

  return (!isEmailInWhitelist("ECOMMERCE_NEW_IHM_WHITE_LIST", user) || !isDesktop) ? (
    <MobileTabs
      activeTab={activeTab}
      {...props}
    />
  ) : (
    <DesktopTabs
      activeTab={activeTab}
      {...props}
    />
  );
};

/* -------------------- HELPERS -------------------- */

export const isUserFranchise = (
  user: User | null
): boolean => {
  const userRoles = user?.fr_cockpit_360 as string[];

  if (!Array.isArray(userRoles)) {
    return false;
  }

  return window?.config?.CREDENTIALS?.USER_FRANCHISE.some(
    (role: string) =>
      userRoles.includes(role)
  );
};

export default TabsBar;